// src/pages/Instagram/ReelsHistoryPage.jsx
import "./ReelsHistoryPage.scss";

import React, { useEffect, useMemo, useState } from "react";

import AppButton from "../../../commons/buttons/AppButton";
import AppInput from "../../../commons/inputs/AppInput";
import AppLoading from "../../../commons/AppLoading";
import HallAppToast from "../../../commons/HallAppToast";
import InstagramAPI from "../InstagramAPI";
import ReportHeader from "../../report-full/components/ReportHeader";
import SelectBasicInput from "../../../commons/inputs/SelectBasicInput";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

// Mock inicial – depois você substitui pela chamada ao backend


const CATEGORY_FILTERS = [
  { label: "Todas as categorias", value: "" },
  { label: "Promoção", value: "PROMO" },
  { label: "Engajamento", value: "ENGAGEMENT" },
  { label: "Antes e Depois", value: "BEFORE_AFTER" },
  { label: "Educacional", value: "TIP" },
  { label: "Prova Social", value: "SOCIAL_PROOF" },
  { label: "Serviço Específico", value: "SERVICE" },
];

const STATUS_FILTERS = [
  { label: "Todos os status", value: "" },
  { label: "Gerado", value: "GENERATED" },
  { label: "Aprovado", value: "APPROVED" },
  { label: "Publicado", value: "PUBLISHED" },
];

function formatDateTime(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function mapStatusLabel(status) {
  switch (status) {
    case "GENERATED":
      return "Gerado";
    case "APPROVED":
      return "Aprovado";
    case "PUBLISHED":
      return "Publicado";
    default:
      return status || "—";
  }
}

const instaAPI = new InstagramAPI();

export default function ReelsHistoryPage() {
  const [loading, setLoading] = useState(true); // depois você integra com o backend
  const [categoryFilter, setCategoryFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [history, setHistory] = useState([]);
  const navigate = useNavigate();

  const errors = {};
  const touched = {};

  useEffect(() => {
    getAllReels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getAllReels = async () => {
    try {
      setLoading(true);
      const response = await instaAPI.getAllReels();
      setHistory(response.data)
    } catch (_) {
      HallAppToast.genericError();
    } finally {
      setLoading(false);
    }
  };

  const filteredList = useMemo(() => {
    return history.filter((item) => {
      if (categoryFilter && item.category !== categoryFilter) return false;
      if (statusFilter && item.status !== statusFilter) return false;

      if (search) {
        const term = search.toLowerCase();
        const text =
          (item.headline || "") +
          " " +
          (item.caption || "") +
          " " +
          (item.script || "");
        if (!text.toLowerCase().includes(term)) return false;
      }

      return true;
    });
  }, [categoryFilter, statusFilter, search, history]);

  const handleCopyCaption = (caption) => {
    if (!caption) return;
    navigator.clipboard
      .writeText(caption)
      .then(() => {
        // Integra com HallAppToast se quiser
        // HallAppToast.success("Legenda copiada para a área de transferência!");
      })
      .catch(() => {
        // HallAppToast.error("Não foi possível copiar a legenda.");
      });
  };

  if (loading) {
    return <AppLoading />;
  }

  return (
    <>
      <ReportHeader name="Histórico" />

      <motion.div
        className="reels-history-container"
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >


        <header className="history-header">

          <div>
            <h2 className="title">Histórico de Reels gerados</h2>
            <p className="subtitle">
              Consulte os roteiros de Reels que já foram gerados pela IA para o seu estabelecimento.
            </p>
          </div>
        </header>

        {/* Filtros usando componentes do HallApp */}
        <section className="history-filters">
          <div className="filter-group">
            <SelectBasicInput
              errors={errors}
              touched={touched}
              name="categoryFilter"
              label="Categoria"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              options={CATEGORY_FILTERS.map((f) => ({
                key: f.value,
                label: f.label,
              }))}
            />
          </div>

          {/* <div className="filter-group">
          <SelectBasicInput
            errors={errors}
            touched={touched}
            name="statusFilter"
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            options={STATUS_FILTERS.map((f) => ({
              key: f.value,
              label: f.label,
            }))}
          />
        </div> */}

          <div className="filter-group filter-group--search">
            <AppInput
              errors={errors}
              touched={touched}
              name="search"
              label="Buscar"
              type="text"
              placeholder="Buscar por título, legenda ou palavra-chave"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </section>

        {filteredList.length === 0 ? (
          <div className="empty-state">
            <p>Nenhum Reels encontrado com os filtros selecionados.</p>
          </div>
        ) : (
          <section className="history-grid">
            {filteredList.map((item) => {
              const scriptPreview = (item.script || "")
                .split("||BREAK||")
                .slice(0, 2);

              const captionPreview =
                (item.caption || "").length > 140
                  ? item.caption.substring(0, 140) + "..."
                  : item.caption;

              return (
                <motion.article
                  key={item.id}
                  className="history-card"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <div className="history-card__header">
                    <span className="chip chip--category">
                      {item.categoryLabel || item.category}
                    </span>
                    <span
                      className={`chip chip--status chip--status-${(
                        item.status || ""
                      ).toLowerCase()}`}
                    >
                      {mapStatusLabel(item.status)}
                    </span>
                  </div>

                  <h3 className="history-card__headline">{item.headline}</h3>

                  <p className="history-card__datetime">
                    {formatDateTime(item.createdAt)}
                  </p>

                  <div className="history-card__block">
                    <p className="history-card__label">Roteiro (prévia):</p>
                    <p className="history-card__text">
                      <div className="history-card__text pre-wrap">
                        {scriptPreview.map((line, idx) => (
                          <React.Fragment key={idx}>
                            {line}
                            <br />
                            <br />
                          </React.Fragment>
                        ))}
                      </div>
                    </p>
                  </div>

                  <div className="history-card__block">
                    <p className="history-card__label">Legenda (prévia):</p>
                    <p className="history-card__text">{captionPreview}</p>
                  </div>

                  {item.hashtags && item.hashtags.length > 0 && (
                    <div className="history-card__block">
                      <p className="history-card__label">Hashtags:</p>
                      <p className="history-card__hashtags">
                        {item.hashtags.join(" ")}
                      </p>
                    </div>
                  )}

                  <div className="history-card__footer">
                    <span className="history-card__id">
                      ID interno: <strong>{item.id}</strong>
                    </span>

                    <div className="history-card__actions">
                      <AppButton
                        type="button"
                        name="Copiar legenda"
                        onClick={() => handleCopyCaption(item.caption)}
                      />
                    </div>
                  </div>
                </motion.article>
              );
            })}
          </section>
        )}
      </motion.div>
    </>
  );
}
